'use client';

/**
 * Shared filter controls for the catalog and the home / landing grids:
 * dropdowns (violet when open, icon menu with a check on the selected option
 * and a white focus ring — design ref Oct 2026), a category chip row that
 * fills the width with a "More" overflow, and the scoped stylesheet that
 * gives them hover / focus states (inline styles would block :hover).
 */
import {
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Check, ChevronDown } from 'lucide-react';

/** Venus / Mars / Transgender glyph for the gender pill. */
export function GenderGlyph({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
  };
  const g = gender.toLowerCase();
  if (g === 'male') {
    return (
      <svg {...common}>
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  if (g === 'trans') {
    return (
      <svg {...common}>
        <circle cx="11" cy="12" r="5" />
        <line x1="11" y1="17" x2="11" y2="22" />
        <line x1="8" y1="20" x2="14" y2="20" />
        <line x1="19" y1="2" x2="13.5" y2="7.5" />
        <polyline points="14 2 19 2 19 7" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="9" r="6" />
      <line x1="12" y1="15" x2="12" y2="22" />
      <line x1="9" y1="19" x2="15" y2="19" />
    </svg>
  );
}

// ── Control styles ──────────────────────────────────────────────────────────
// Layout stays inline (independent of Tailwind JIT); colours live in the
// scoped stylesheet below so :hover / :focus can change them — an inline
// background would override any hover class.
export const control = (): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  height: '42px',
  borderRadius: '12px',
  padding: '0 14px',
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
});
export const chip = (): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  borderRadius: '9999px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
});

const CONTROL_CSS = `
.cat-control {
  color: #fff;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  transition: background .18s ease, border-color .18s ease, transform .18s ease, box-shadow .18s ease;
}
.cat-control:hover {
  color: #fff;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.02) 100%),
    rgba(236,72,153,0.55);
  border-color: rgba(255,255,255,0.20);
  transform: translateY(-1px);
  box-shadow: 0 10px 22px -12px rgba(236,72,153,0.85);
}
input.cat-control:hover {
  background: rgba(255,255,255,0.06);
  color: #fff;
  border-color: rgba(255,255,255,0.18);
  box-shadow: none;
}
/* Icons follow the text colour on hover (they carry their own white tint). */
.cat-control:hover svg { color: #fff; }
.cat-chip:hover svg { color: #ff9ed2; }
.cat-control[data-active="true"] {
  background: rgba(236,72,153,0.12);
  border-color: rgba(236,72,153,0.45);
}
input.cat-control:hover { transform: none; }
input.cat-control:focus {
  outline: none;
  border-color: rgba(236,72,153,0.55);
  background: rgba(255,255,255,0.07);
}
.cat-chip {
  color: rgba(255,255,255,0.82);
  background: rgba(255,255,255,0.045);
  transition: background .18s ease, color .18s ease, transform .18s ease, box-shadow .18s ease;
}
.cat-chip:hover {
  color: #ff9ed2;
  background: rgba(236,72,153,0.12);
  box-shadow: inset 0 0 0 1px rgba(236,72,153,0.45);
  transform: translateY(-1px);
}
.cat-chip[data-active="true"] {
  color: #fff;
  background: rgba(255,255,255,0.10);
  box-shadow: inset 0 -2px 0 rgba(236,72,153,0.6), 0 8px 18px -10px rgba(236,72,153,0.9);
}
.cat-control[data-open="true"], .cat-control[data-open="true"]:hover {
  color: #fff;
}
.cat-control[data-open="true"]:hover svg { color: #fff; }
.cat-control[data-open="true"] {
  background: linear-gradient(180deg, #f472b6 0%, #db2777 100%);
  border-color: rgba(255,255,255,0.22);
  box-shadow: 0 10px 24px -12px rgba(236,72,153,0.9);
  transform: none;
}
.cat-menu {
  background: rgba(28,25,36,0.97);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 18px;
  padding: 8px;
  box-shadow: 0 24px 48px -16px rgba(0,0,0,0.75);
  backdrop-filter: blur(14px);
}
.cat-option {
  display: flex; width: 100%; align-items: center; gap: 14px;
  min-height: 50px; padding: 0 16px; border-radius: 14px;
  font-size: 15px; color: rgba(255,255,255,0.88); text-align: left;
  transition: background .15s ease, box-shadow .15s ease, color .15s ease;
}
.cat-option:hover, .cat-option:focus-visible {
  outline: none; color: #fff;
  background: rgba(255,255,255,0.05);
  box-shadow: inset 0 0 0 2px rgba(255,255,255,0.92);
}
.cat-option[data-selected="true"] {
  color: #ff9ed2;
  background: rgba(236,72,153,0.14);
}
.cat-option[data-selected="true"] svg { color: #ec4899; }
.cat-option:active { background: rgba(236,72,153,0.22); }
.cat-control, .cat-chip, .cat-option { -webkit-tap-highlight-color: transparent; }
.cat-chip:active, button.cat-control:active { background: rgba(236,72,153,0.22); }
@media (min-width: 768px) {
  .filter-row { grid-template-columns: 2fr 1fr 1fr 1fr 1fr; gap: 8px; }
}
/* Phones: three dropdowns share a row, so tighten them (inline layout
   styles need !important to be overridden). */
@media (max-width: 767px) {
  button.cat-control {
    padding: 0 8px !important;
    gap: 5px !important;
    font-size: 13px !important;
    height: 40px !important;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cat-control, .cat-chip { transition: none; }
  .cat-control:hover, .cat-chip:hover { transform: none; }
}
`;
const menuStyle = (open: boolean): CSSProperties => ({
  minWidth: '190px',
  opacity: open ? 1 : 0,
  transform: open ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)',
  transformOrigin: 'top center',
  pointerEvents: open ? 'auto' : 'none',
  transition: 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.16,1,0.3,1)',
});

/** Mount once per page that uses these controls. */
export function FilterStyles() {
  return <style>{CONTROL_CSS}</style>;
}

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

function MenuOption({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string;
  icon?: ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      data-selected={selected}
      onClick={onClick}
      className="cat-option"
    >
      {icon && (
        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-white/80">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {selected && <Check className="h-4 w-4 flex-shrink-0" />}
    </button>
  );
}

/**
 * A filter dropdown. The button shows the icon and label of the current
 * value; the menu lists every option with its icon and a check on the
 * selected one.
 */
export function FilterDropdown<T extends string>({
  icon,
  label,
  active,
  open,
  onToggle,
  options,
  value,
  onSelect,
  align = 'left',
}: {
  icon: ReactNode;
  label: string;
  /** A non-default value is applied (tints the button). */
  active: boolean;
  open: boolean;
  onToggle: () => void;
  options: FilterOption<T>[];
  value: T;
  onSelect: (value: T) => void;
  align?: 'left' | 'right';
}) {
  return (
    <div className="relative min-w-0" style={{ zIndex: open ? 60 : 30 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...control(), width: '100%' }}
        className="cat-control"
        data-active={active}
        data-open={open}
      >
        {icon}
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        <ChevronDown
          className="h-4 w-4 text-white/70"
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </button>
      <div
        role="menu"
        className={`cat-menu absolute top-full z-50 mt-2 w-full ${
          align === 'right' ? 'right-0' : 'left-0'
        }`}
        style={menuStyle(open)}
        aria-hidden={!open}
      >
        {options.map((o) => (
          <MenuOption
            key={o.value}
            label={o.label}
            icon={o.icon}
            selected={o.value === value}
            onClick={() => onSelect(o.value)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One row of category chips that always fills the width: chips are measured
 * off-screen and as many as fit are shown, the rest go under "More", pinned
 * to the right edge. Re-measures on resize.
 */
export function ChipRow({
  categories,
  activeKey,
  moreOpen,
  onToggleMore,
  onSelect,
  trailing,
}: {
  categories: { key: string; label: string }[];
  activeKey: string;
  moreOpen: boolean;
  onToggleMore: () => void;
  onSelect: (key: string) => void;
  /** Pinned after More at the far right (e.g. "View all"). */
  trailing?: ReactNode;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const trailingRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(categories.length);
  // Re-measure only when the set of chips changes, not on every render.
  const chipKey = categories.map((c) => c.key).join('|');

  // Layout measurement has to run after paint and on every resize.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const GAP = 8;
    const compute = () => {
      const widths = [...measure.children].map(
        (el) => (el as HTMLElement).offsetWidth,
      );
      const moreWidth = widths.pop() ?? 90;
      // Hidden trailing content (e.g. View all on phones) measures 0.
      const trailingBox = trailingRef.current?.offsetWidth ?? 0;
      const trailingWidth = trailingBox ? trailingBox + GAP : 0;
      const available = row.clientWidth - trailingWidth;
      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        const next = used + (i ? GAP : 0) + widths[i]!;
        const needsMore = i < widths.length - 1;
        if (next + (needsMore ? GAP + moreWidth : 0) > available) break;
        used = next;
        count = i + 1;
      }
      setFit(Math.max(1, count));
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(row);
    return () => observer.disconnect();
  }, [chipKey]);

  const shown = categories.slice(0, fit);
  const hidden = categories.slice(fit);

  return (
    <div ref={rowRef} className="relative z-20 flex items-center gap-2 py-1">
      {/* Off-screen copy used only for measuring chip widths. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none absolute flex gap-2"
        style={{ visibility: 'hidden', left: 0, top: 0 }}
      >
        {categories.map((cat) => (
          <span key={cat.key} style={chip()} className="cat-chip">
            {cat.label}
          </span>
        ))}
        <span style={chip()} className="cat-chip">
          More <ChevronDown className="h-4 w-4" />
        </span>
      </div>

      {shown.map((cat) => (
        <button
          key={cat.key}
          type="button"
          onClick={() => onSelect(cat.key)}
          style={chip()}
          className="cat-chip flex-shrink-0"
          data-active={activeKey === cat.key}
        >
          {cat.label}
        </button>
      ))}

      {hidden.length > 0 && (
        <div className="relative ml-auto flex-shrink-0">
          <button
            type="button"
            onClick={onToggleMore}
            style={chip()}
            className="cat-chip"
            data-active={hidden.some((c) => c.key === activeKey)}
          >
            More
            <ChevronDown
              className="h-4 w-4"
              style={{
                transition: 'transform 0.2s',
                transform: moreOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </button>
          <div
            className="cat-menu absolute top-full right-0 z-50 mt-2 max-h-80 overflow-y-auto"
            style={menuStyle(moreOpen)}
            aria-hidden={!moreOpen}
          >
            {hidden.map((cat) => (
              <MenuOption
                key={cat.key}
                label={cat.label}
                selected={activeKey === cat.key}
                onClick={() => onSelect(cat.key)}
              />
            ))}
          </div>
        </div>
      )}

      {trailing && (
        <div
          ref={trailingRef}
          className={`flex-shrink-0 ${hidden.length > 0 ? '' : 'ml-auto'}`}
        >
          {trailing}
        </div>
      )}
    </div>
  );
}
